/**
 * @file routes/(public)/public/cert-search.tsx
 * @description 公開證書查詢頁面路由（無需認證）。
 *
 * 設計：對外品牌風格（綠色有機調 + 沉穩深綠 header）
 *   - Hero 區大字標題 + 主搜尋
 *   - 結果區：狀態橫幅 + DataFieldGrid 詳情
 *   - 頁尾：機構資訊 + 免責聲明
 *
 * 功能：
 * - 輸入證書編號或公司名稱即時查詢 ZHCB 核發之證書
 * - 調用真實公開 API：GET /api/public/cert-search?q=...
 * - 查詢結果以狀態橫幅 + 雙欄詳情 DataFieldGrid 展示
 * - 查無結果顯示 Result 空狀態
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, Input, Result } from 'antd';
import { message } from '@/lib/antd-app';
import {
  Award,
  Building2,
  Calendar,
  CheckCircle2,
  FileText,
  Leaf,
  MapPin,
  Phone,
  Search,
  ShieldCheck,
  Sprout,
  XCircle,
} from 'lucide-react';
import { DataField, DataFieldGrid, StatusPill } from '@/components/shell';
import {
  color,
  font,
  leading,
  radius,
  shadow,
  space,
  text,
  tracking,
  weight,
} from '@/styles/tokens';

/** 路由定義 */
export const Route = createFileRoute('/(public)/public/cert-search')({
  component: CertSearchPage,
});

/* ════════════════════════════════════════════════
 * 型別定義
 * ════════════════════════════════════════════════ */

/**
 * UI 層展示用的證書結果型別。
 */
interface CertificateResult {
  certNumber: string;
  companyName: string;
  scheme: string;
  scope: string;
  issueDate: string;
  expiryDate: string;
  status: 'valid' | 'expired' | 'suspended' | 'revoked';
  category: string;
  address: string;
  phone: string;
  area: string;
  products: string[];
}

/**
 * 後端 publicCertSearch 回傳格式。
 */
interface ApiCertResult {
  certNo: string;
  clientName: string;
  scheme: string | null;
  validFrom: string | null;
  validUntil: string | null;
  status: string;
  productScope: Array<{ product: string; area?: string; unit?: string }> | null;
}

/* ════════════════════════════════════════════════
 * 常量配置
 * ════════════════════════════════════════════════ */

/** 證書狀態 → StatusPill tone + 顯示文字 + 說明 */
const STATUS_DISPLAY: Record<
  CertificateResult['status'],
  {
    label: string;
    tone: 'success' | 'danger' | 'warning';
    description: string;
  }
> = {
  valid: {
    label: '證書有效',
    tone: 'success',
    description: '此證書目前有效，持證者已通過 ZHCB 驗證機構之驗證。',
  },
  expired: {
    label: '證書已過期',
    tone: 'danger',
    description: '此證書已過期，請聯繫驗證機構瞭解續約狀態。',
  },
  suspended: {
    label: '證書已暫停',
    tone: 'warning',
    description: '此證書目前已暫停效力，請聯繫驗證機構確認詳情。',
  },
  revoked: {
    label: '證書已撤銷',
    tone: 'danger',
    description: '此證書已被撤銷，不再具備驗證效力。',
  },
};

/** 公開頁面用的綠色品牌色（此頁面專屬） */
const BRAND_GREEN = {
  primary: color.emerald[700],
  primaryDark: '#0d3820',
  primarySoft: color.emerald[50],
  primarySoftHover: color.emerald[100],
  surface: '#f6fbf6',
};

/* ════════════════════════════════════════════════
 * 資料轉換
 * ════════════════════════════════════════════════ */

/**
 * 將後端 API 欄位對應到 UI 展示型別。
 * 缺少的欄位（address / phone）以空字串帶入，UI 層 DataField 會顯示 —。
 */
function mapApiToCertResult(r: ApiCertResult): CertificateResult {
  const statusMap: Record<string, CertificateResult['status']> = {
    有效: 'valid',
    即將到期: 'valid',
    已過期: 'expired',
    已暫停: 'suspended',
    已撤銷: 'revoked',
  };

  const products = r.productScope ? r.productScope.map((p) => p.product) : [];
  const area = r.productScope
    ? r.productScope
        .filter((p) => p.area)
        .map((p) => `${p.area}`)
        .join('、') || undefined
    : undefined;

  return {
    certNumber: r.certNo,
    companyName: r.clientName,
    scheme: r.scheme ?? '有機農產品',
    scope: products.join('、') || '—',
    issueDate: r.validFrom ? r.validFrom.slice(0, 10) : '—',
    expiryDate: r.validUntil ? r.validUntil.slice(0, 10) : '—',
    status: statusMap[r.status] ?? 'valid',
    category: '農糧產品',
    address: '',
    phone: '',
    area: area ?? '',
    products,
  };
}

/* ════════════════════════════════════════════════
 * 頁面
 * ════════════════════════════════════════════════ */

/**
 * 公開證書查詢頁面。
 * 對外品牌風格，不使用後台 PageShell。
 * 搜尋結果取 API 回傳陣列第一筆，以詳情卡片形式展示。
 */
function CertSearchPage() {
  const [searchText, setSearchText] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [queryResult, setQueryResult] = useState<CertificateResult | null>(null);
  const [loading, setLoading] = useState(false);

  /**
   * 執行查詢：呼叫真實公開 API GET /api/public/cert-search。
   * 結果取陣列第一筆映射為 UI 型別；查無結果則 queryResult 為 null。
   */
  const handleSearch = async (value: string) => {
    const keyword = value.trim();
    if (!keyword) {
      message.warning('請輸入證書編號或公司名稱');
      return;
    }
    setLoading(true);
    setHasSearched(true);
    try {
      const params = new URLSearchParams({ q: keyword });
      const res = await fetch(`/api/public/cert-search?${params.toString()}`, {
        credentials: 'same-origin',
      });
      const json = (await res.json()) as {
        success: boolean;
        data: ApiCertResult[] | ApiCertResult;
      };
      if (!json.success) {
        setQueryResult(null);
      } else {
        /* cert-search 回傳陣列，取第一筆 */
        const raw = Array.isArray(json.data) ? json.data[0] : json.data;
        setQueryResult(raw ? mapApiToCertResult(raw) : null);
      }
    } catch (err) {
      console.error('[cert-query] API 查詢失敗:', err);
      message.error('查詢服務暫時不可用，請稍後再試');
      setQueryResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${BRAND_GREEN.surface} 0%, #ffffff 60%)`,
        position: 'relative',
      }}
    >
      {/* 裝飾層：頂部漸層光暈 */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(ellipse 80% 40% at 50% -10%, rgba(5, 150, 105, 0.18) 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* ═══ 頂部品牌列 ═══ */}
      <header
        style={{
          background: BRAND_GREEN.primaryDark,
          color: '#fff',
          padding: '14px 0',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div
          style={{
            maxWidth: 1080,
            margin: '0 auto',
            padding: `0 ${space[6]}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: space[4],
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: space[3] }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: radius.md,
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4)',
              }}
            >
              <Leaf size={22} color={BRAND_GREEN.primary} strokeWidth={2.2} />
            </div>
            <div>
              <div
                style={{
                  fontFamily: font.display,
                  fontSize: text.lg,
                  fontWeight: weight.bold,
                  letterSpacing: tracking.tight,
                  lineHeight: 1.25,
                }}
              >
                ZHCB 驗證機構
              </div>
              <div
                style={{
                  fontSize: text.xs,
                  color: 'rgba(255,255,255,0.7)',
                  textTransform: 'uppercase',
                  letterSpacing: tracking.widest,
                  fontWeight: weight.semibold,
                  marginTop: 2,
                }}
              >
                證書公開查詢系統
              </div>
            </div>
          </div>
          <a
            href="/login"
            style={{
              fontSize: text.sm,
              color: 'rgba(255,255,255,0.7)',
              textDecoration: 'none',
              padding: '6px 12px',
              borderRadius: radius.full,
              border: '1px solid rgba(255,255,255,0.15)',
              transition: 'all 120ms cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
            }}
          >
            機構登入
          </a>
        </div>
      </header>

      {/* ═══ Hero + 搜尋 ═══ */}
      <main
        style={{
          maxWidth: 1080,
          margin: '0 auto',
          padding: `${space[12]}px ${space[6]}px ${space[16]}px`,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <section
          style={{
            textAlign: 'center',
            marginBottom: space[8],
          }}
        >
          {/* 上方標籤（eyebrow） */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: radius.full,
              background: BRAND_GREEN.primarySoft,
              color: BRAND_GREEN.primary,
              fontSize: text.xs,
              fontWeight: weight.semibold,
              letterSpacing: tracking.wider,
              textTransform: 'uppercase',
              marginBottom: space[5],
            }}
          >
            <ShieldCheck size={13} />
            ISO/IEC 17065 認可機構
          </div>

          {/* 大標 */}
          <h1
            style={{
              margin: 0,
              fontFamily: font.display,
              fontSize: text['4xl'],
              fontWeight: weight.extrabold,
              color: color.text.primary,
              letterSpacing: tracking.tight,
              lineHeight: 1.15,
              marginBottom: space[3],
            }}
          >
            驗證證書真實性查詢
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: text.lg,
              color: color.text.tertiary,
              maxWidth: 560,
              marginInline: 'auto',
              lineHeight: leading.snug,
              marginBottom: space[8],
            }}
          >
            輸入證書編號或公司名稱，即時查驗 ZHCB 驗證機構核發之證書狀態與內容。
          </p>

          {/* 搜尋區（卡片風） */}
          <div
            style={{
              maxWidth: 640,
              marginInline: 'auto',
              background: '#fff',
              borderRadius: radius.xl,
              boxShadow: shadow.lg,
              border: `1px solid ${color.border.subtle}`,
              padding: space[6],
              textAlign: 'left',
            }}
          >
            <Input.Search
              placeholder="例如：CERT-2026-TOP0001-01 或「宜蘭水稻有機農場」"
              enterButton={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Search size={14} />
                  查詢
                </span>
              }
              size="large"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              loading={loading}
              allowClear
              style={{ width: '100%' }}
            />

            {/* 範例查詢 chips */}
            <div
              style={{
                marginTop: space[4],
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: space[2],
              }}
            >
              <span
                style={{
                  fontSize: text.sm,
                  color: color.text.muted,
                  letterSpacing: tracking.wide,
                  fontWeight: weight.medium,
                }}
              >
                試試查詢：
              </span>
              {['CERT-2026-TOP0001-01', '宜蘭水稻有機農場', '有機'].map((example) => (
                <button
                  key={example}
                  type="button"
                  onClick={() => {
                    setSearchText(example);
                    void handleSearch(example);
                  }}
                  style={{
                    fontFamily: example.startsWith('CERT') ? font.mono : font.body,
                    fontSize: text.sm,
                    color: BRAND_GREEN.primary,
                    background: BRAND_GREEN.primarySoft,
                    border: `1px solid ${BRAND_GREEN.primarySoft}`,
                    padding: '4px 10px',
                    borderRadius: radius.full,
                    cursor: 'pointer',
                    transition: 'all 120ms cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = BRAND_GREEN.primarySoftHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = BRAND_GREEN.primarySoft;
                  }}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ 查詢結果 ═══ */}
        {hasSearched && !loading && (
          <section
            style={{
              maxWidth: 880,
              marginInline: 'auto',
              background: '#fff',
              borderRadius: radius.xl,
              boxShadow: shadow.md,
              border: `1px solid ${color.border.subtle}`,
              overflow: 'hidden',
            }}
          >
            {queryResult ? (
              <>
                {/* 狀態橫幅 */}
                <div
                  style={{
                    padding: `${space[5]}px ${space[6]}px`,
                    background:
                      queryResult.status === 'valid'
                        ? color.state.successSoft
                        : color.state.dangerSoft,
                    borderBottom: `1px solid ${color.border.subtle}`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: space[4],
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: radius.full,
                      background:
                        queryResult.status === 'valid' ? color.state.success : color.state.danger,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {queryResult.status === 'valid' ? (
                      <CheckCircle2 size={24} strokeWidth={2.4} />
                    ) : (
                      <XCircle size={24} strokeWidth={2.4} />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: space[2],
                        marginBottom: 4,
                      }}
                    >
                      <h2
                        style={{
                          margin: 0,
                          fontFamily: font.display,
                          fontSize: text.xl,
                          fontWeight: weight.bold,
                          color: color.text.primary,
                          letterSpacing: tracking.tight,
                        }}
                      >
                        {STATUS_DISPLAY[queryResult.status].label}
                      </h2>
                      <StatusPill tone={STATUS_DISPLAY[queryResult.status].tone} dot>
                        {STATUS_DISPLAY[queryResult.status].label.replace('證書', '')}
                      </StatusPill>
                    </div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: text.md,
                        color: color.text.tertiary,
                        lineHeight: leading.snug,
                      }}
                    >
                      {STATUS_DISPLAY[queryResult.status].description}
                    </p>
                  </div>
                </div>

                {/* 詳情：兩欄 DataFieldGrid */}
                <div style={{ padding: `${space[6]}px ${space[6]}px ${space[5]}px` }}>
                  <DataFieldGrid columns={2}>
                    <DataField
                      icon={<FileText size={12} />}
                      label="證書編號"
                      value={queryResult.certNumber}
                      mono
                    />
                    <DataField
                      icon={<Award size={12} />}
                      label="證書狀態"
                      value={
                        <StatusPill tone={STATUS_DISPLAY[queryResult.status].tone} dot>
                          {STATUS_DISPLAY[queryResult.status].label.replace('證書', '')}
                        </StatusPill>
                      }
                    />
                    <DataField
                      icon={<Building2 size={12} />}
                      label="公司名稱"
                      value={queryResult.companyName}
                      span={2}
                    />
                    <DataField
                      icon={<Sprout size={12} />}
                      label="驗證方案"
                      value={
                        <StatusPill tone="success" variant="subtle">
                          {queryResult.scheme}
                        </StatusPill>
                      }
                    />
                    <DataField label="驗證類別" value={queryResult.category} />
                    <DataField label="驗證範圍" value={queryResult.scope} span={2} />
                    <DataField label="驗證面積" value={queryResult.area} />
                    <DataField
                      icon={<Calendar size={12} />}
                      label="發證日期"
                      value={queryResult.issueDate}
                      mono
                    />
                    <DataField
                      label="到期日期"
                      value={
                        <span
                          style={{
                            fontFamily: font.mono,
                            color:
                              queryResult.status === 'valid'
                                ? color.text.primary
                                : color.state.danger,
                            fontWeight: weight.semibold,
                          }}
                        >
                          {queryResult.expiryDate}
                        </span>
                      }
                    />
                    <DataField
                      icon={<MapPin size={12} />}
                      label="場址地址"
                      value={queryResult.address}
                      span={2}
                    />
                    <DataField
                      icon={<Phone size={12} />}
                      label="聯絡電話"
                      value={queryResult.phone}
                      mono
                    />
                    <DataField
                      label="驗證產品"
                      value={
                        queryResult.products.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {queryResult.products.map((p) => (
                              <StatusPill key={p} tone="success" variant="outline" size="sm">
                                {p}
                              </StatusPill>
                            ))}
                          </div>
                        ) : null
                      }
                      span={2}
                    />
                  </DataFieldGrid>

                  {/* 免責聲明 */}
                  <p
                    style={{
                      margin: `${space[6]}px 0 0`,
                      paddingTop: space[4],
                      borderTop: `1px solid ${color.border.subtle}`,
                      fontSize: text.sm,
                      color: color.text.muted,
                      textAlign: 'center',
                      lineHeight: leading.snug,
                    }}
                  >
                    本查詢結果僅供參考，證書實際狀態以驗證機構核發之正式文件為準。 如有疑問請洽 ZHCB
                    驗證機構客服專線。
                  </p>
                </div>
              </>
            ) : (
              <div style={{ padding: `${space[12]}px ${space[6]}px` }}>
                <Result
                  icon={
                    <div
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        background: color.state.dangerSoft,
                        color: color.state.danger,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <XCircle size={36} strokeWidth={1.6} />
                    </div>
                  }
                  title={
                    <span
                      style={{
                        fontFamily: font.display,
                        fontSize: text.xl,
                        fontWeight: weight.semibold,
                        letterSpacing: tracking.tight,
                      }}
                    >
                      未找到相關證書
                    </span>
                  }
                  subTitle={
                    <span style={{ fontSize: text.md, color: color.text.tertiary }}>
                      未找到與「{searchText}
                      」相關的證書資料，請確認輸入的證書編號或公司名稱是否正確。
                    </span>
                  }
                  extra={
                    <Button
                      type="primary"
                      size="large"
                      style={{
                        background: BRAND_GREEN.primary,
                        borderColor: BRAND_GREEN.primary,
                      }}
                      onClick={() => {
                        setSearchText('');
                        setHasSearched(false);
                        setQueryResult(null);
                      }}
                    >
                      重新查詢
                    </Button>
                  }
                />
              </div>
            )}
          </section>
        )}
      </main>

      {/* ═══ 頁尾 ═══ */}
      <footer
        style={{
          borderTop: `1px solid ${color.border.subtle}`,
          padding: `${space[8]}px ${space[6]}px`,
          textAlign: 'center',
          background: '#fff',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div
          style={{
            maxWidth: 1080,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: space[2],
          }}
        >
          <div
            style={{
              fontFamily: font.display,
              fontSize: text.md,
              fontWeight: weight.semibold,
              color: color.text.secondary,
              letterSpacing: tracking.tight,
            }}
          >
            ZHCB 驗證機構
          </div>
          <div
            style={{
              fontSize: text.sm,
              color: color.text.tertiary,
              lineHeight: leading.snug,
            }}
          >
            地址：臺北市中正區驗證路 1 號 | 電話：02-1234-5678 | Email：service@zhcb.org.tw
          </div>
          <div
            style={{
              fontSize: text.xs,
              color: color.text.muted,
              marginTop: space[2],
              letterSpacing: tracking.wide,
            }}
          >
            &copy; {new Date().getFullYear()} ZHCB Certification Body. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
